import { computed, getCurrentInstance, nextTick } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import randomUUID from '@/utils/uuid'
import workflowApi from '@/api/workflowApi'
import { scrollToEvent } from '@/utils/scroll'
import safeStructuredClone from '@/utils/clone'
import { SchemaType } from '@/constants/schema'
import { cardHelpers } from '@/mixins/cardHelpers'
import { toCamelCase } from '@/shared/codegen/utils'
import { findEntityIdByName } from '@/utils/fieldNameMatch'
import { DomainModelRole } from '@/constants/cardTypes'
import { workflowHelpers } from '@/mixins/workflowHelpers'
import { sanitizeCardDescription } from '@/utils/sanitize'
import { isFileRelevantForAnalysis } from '@/utils/fileContent'
import { parseEntityResponse } from '@/shared/utils/workflowSpec'
import { generateSortEvents, generateWorkflowFromCode, generateEntitiesExampleData, generateFileTagging } from '@/ai'
import {
  formatCardKey,
  convertFieldsToSchema,
  convertReadModelFieldsToSchema,
  convertDomainEventFieldsToSchema
} from '@/ai/utils/helpers'

const useGenerateWorkflow = () => {
  const store = useStore()
  const route = useRoute()
  const router = useRouter()
  const { proxy } = getCurrentInstance()
  const workflow = computed(() => store.state.workflow.workflow)
  const cardTypes = computed(() => store.state.cardTypes.cardTypes)
  const svgGraph = computed(() => store.getters['svgGraphData/svgGraphData'])
  const svgLanes = computed(() => store.getters['svgGraphData/svgLanesData'])
  const svgGroups = computed(() => store.getters['svgGraphData/svgGroupsData'])
  const failedGenerations = computed(() => store.state.workflow.failedGenerations)

  const addEvent = async (eventFromLLM) => {
    const oldVersion = workflow.value.version
    const shape = { description: eventFromLLM.description }
    const options = { laneName: eventFromLLM.role, createNewLaneIfNoLaneId: true }

    await nextTick()
    const selectedEventId = route.params.eventId && svgGraph.value[route.params.eventId]?.id
    const parentId = options.parentEventId || selectedEventId || 'start'
    if (parentId !== 'start' && workflow.value?.domainEvents?.findIndex((event) => event.id === parentId) === -1) {
      return
    }
    shape.parents = [parentId]
    const newEventId = await workflowApi.addEventToWorkflow(
      shape,
      options,
      svgLanes.value,
      svgGraph.value,
      svgGroups.value,
      workflow.value
    )
    await updateRouteWithEventId(newEventId)
    await ensureNextVersion(oldVersion)
  }

  const addCard = async (cardFromLLM) => {
    const event = getEventFromEventObj(cardFromLLM)

    if (!event) {
      return
    }

    for (let i = 0; i < cardTypes.value?.length; i++) {
      if (cardTypes.value[i].generateWithAI) {
        const cardType = formatCardKey(cardTypes.value[i].title)

        if (!cardFromLLM[cardType]) {
          continue
        }

        if (!Array.isArray(cardFromLLM[cardType])) {
          cardFromLLM[cardType] = [cardFromLLM[cardType]]
        }

        for (const cardData of cardFromLLM[cardType]) {
          const oldVersion = workflow.value.version
          await updateRouteWithEventId(event.id)

          const cardText = typeof cardData === 'object' ? cardData.name : cardData

          const index = cardTypes.value.findIndex((type) => type.title === cardTypes.value[i].title)
          if (index >= 0) {
            const cardTypeId = cardTypes.value[index].id
            const eventId = event.id || route.params.eventId
            await workflowApi.createCard(route.params.id, eventId, {
              cardTypeId,
              description: cardText
            })
            await ensureNextVersion(oldVersion)
          }
        }
      }
    }
  }

  const addCommandSchema = async (commandFromLLM) => {
    const oldVersion = workflow.value.version
    const event = getEventFromEventObj(commandFromLLM)

    if (!event) {
      return
    }

    const commandCard = cardHelpers.methods.getCommandCard(event)

    if (commandFromLLM.fields?.length > 0) {
      await updateRouteWithEventId(event.id)

      const { fields, cardId, commandName } = commandFromLLM
      const name = commandName || sanitizeCardDescription(commandCard?.description) || ''
      const newFields = convertFieldsToSchema(fields)
      const targetCardId = cardId || commandCard?.id || null

      await workflowApi.addSchemaAndLinkCard({
        schema: {
          name,
          type: SchemaType.COMMAND,
          fields: newFields
        },
        cardId: targetCardId,
        eventId: event.id,
        workflow: workflow.value,
        svgGraph: svgGraph.value
      })
      await ensureNextVersion(oldVersion)
    }
  }

  const addQuerySchema = async (queryFromLLM) => {
    const oldVersion = workflow.value.version
    const event = getEventFromEventObj(queryFromLLM)

    if (!event) {
      return
    }

    const queryCards = event ? cardHelpers.methods.getQueryCards(event) : []
    const queryCard = queryFromLLM.queryName
      ? queryCards.find((card) => sanitizeCardDescription(card.description) === queryFromLLM.queryName)
      : queryCards[0]

    if (queryFromLLM.queryFields?.length > 0) {
      await updateRouteWithEventId(event.id)

      const { queryFields, cardId, queryName, queriedEntity, cardinality } = queryFromLLM
      const name = queryName || sanitizeCardDescription(queryCard?.description) || ''

      const newFields = convertReadModelFieldsToSchema(queryFields)
      const targetCardId = cardId || queryCard?.id || null

      await workflowApi.addSchemaAndLinkCard({
        schema: {
          name,
          type: SchemaType.QUERY,
          cardinality,
          fields: newFields
        },
        cardId: targetCardId,
        eventId: event.id,
        workflow: workflow.value,
        svgGraph: svgGraph.value
      })
      await ensureNextVersion(oldVersion)

      return {
        queryName: name,
        queriedEntity
      }
    }

    return null
  }

  const addDomainEventSchema = async (domainEventFromLLM) => {
    const oldVersion = workflow.value.version
    const event = getEventFromEventObj(domainEventFromLLM)

    if (!event) {
      return null
    }

    const domainEventCard = cardHelpers.methods.getDomainEventCard(event)

    if (domainEventFromLLM.domainEventFields?.length > 0) {
      await updateRouteWithEventId(event.id)

      const { domainEventFields, domainEventName, domainEventEntity } = domainEventFromLLM
      const name = domainEventName || sanitizeCardDescription(domainEventCard?.description) || event.description || ''
      const newFields = convertDomainEventFieldsToSchema(domainEventFields)
      const targetCardId = domainEventCard?.id || null

      await workflowApi.addSchemaAndLinkCard({
        schema: {
          name,
          type: SchemaType.DOMAIN_EVENT,
          fields: newFields
        },
        cardId: targetCardId,
        eventId: event.id,
        workflow: workflow.value,
        svgGraph: svgGraph.value
      })
      await ensureNextVersion(oldVersion)

      return {
        domainEventName: name,
        domainEventEntity
      }
    }

    return null
  }

  const addEntities = async (entitiesFromLLM) => {
    if (!entitiesFromLLM?.length) {
      return
    }

    let schemas = safeStructuredClone(workflow.value.schemas || [])
    const existingEntityNames = new Set(
      schemas.filter((s) => s.type === SchemaType.ENTITY).map((s) => s.name.toLowerCase())
    )

    const newEntities = entitiesFromLLM.filter(
      (entity) => !existingEntityNames.has(entity.name.toLowerCase())
    )

    const uniqueEntities = []
    const entityNames = new Set()

    for (const entity of newEntities) {
      const nameLower = entity.name.toLowerCase()

      if (!entityNames.has(nameLower)) {
        entityNames.add(nameLower)
        uniqueEntities.push(entity)
      }
    }

    if (uniqueEntities.length === 0) {
      return
    }

    const entityNameToIdMap = new Map()

    for (const schema of schemas) {
      if (schema.type === SchemaType.ENTITY) {
        entityNameToIdMap.set(schema.name.toLowerCase(), schema.id)
      }
    }

    const newEntityUUIDs = new Map()
    for (const entity of uniqueEntities) {
      const uuid = randomUUID()
      newEntityUUIDs.set(entity.name, uuid)
      entityNameToIdMap.set(entity.name.toLowerCase(), uuid)
    }

    for (const entityFromLLM of uniqueEntities) {
      const generatedEntity = parseEntityResponse(entityFromLLM, entityNameToIdMap)
      generatedEntity.id = newEntityUUIDs.get(entityFromLLM.name)
      schemas = [...schemas, generatedEntity]
    }

    if (schemas.length > 0) {
      await workflowApi.updateSchemas({
        schemas,
        workflow: workflow.value
      })
    }
  }

  const linkEntities = async (queriesForLinking = [], domainEventsForLinking = []) => {
    const entities = (workflow.value.schemas || []).filter((s) => s.type === SchemaType.ENTITY)
    const chunkSize = 5
    const events = workflow.value.domainEvents
    const updatedSchemas = safeStructuredClone(workflow.value.schemas || [])
    let schemasNeedUpdate = false

    for (let i = 0; i < events.length; i += chunkSize) {
      const chunk = events.slice(i, i + chunkSize)
      const updatedSvgGraph = safeStructuredClone(svgGraph.value)
      if (!updatedSvgGraph) {
        continue
      }

      chunk.forEach((event) => {
        if (!updatedSvgGraph[event.id]) {
          return
        }
        updatedSvgGraph[event.id].cards = updatedSvgGraph[event.id].cards?.map((item) => {
          // For AggregateRoot cards: set schemaId to the entity
          if (item.cardType?.domainModelRole === DomainModelRole.AGGREGATE_ROOT) {
            const entityId = entities.find((entity) => entity.name === item.description)?.id

            return {
              ...item,
              schemaId: item.schemaId || entityId
            }
          }

          // For ReadModel cards: update the Query schema's entityId
          if (item.cardType?.domainModelRole === DomainModelRole.READ_MODEL && item.schemaId) {
            const matchingQuery = queriesForLinking.find(
              (q) => q.queryName === sanitizeCardDescription(item.description))
            if (matchingQuery?.queriedEntity) {
              const entityId = entities.find((entity) => entity.name === matchingQuery.queriedEntity)?.id
              if (entityId) {
                const schemaIndex = updatedSchemas.findIndex((s) => s.id === item.schemaId)
                if (schemaIndex !== -1 && !updatedSchemas[schemaIndex].entityId) {
                  updatedSchemas[schemaIndex].entityId = entityId
                  schemasNeedUpdate = true
                }
              }
            }
          }

          // For DomainEvent cards: update the DomainEvent schema's entityId
          if (item.cardType?.domainModelRole === DomainModelRole.DOMAIN_EVENT && item.schemaId) {
            const matchingDomainEvent = domainEventsForLinking.find(
              (de) => de.domainEventName === sanitizeCardDescription(item.description))
            if (matchingDomainEvent?.domainEventEntity) {
              const entityId = entities.find((entity) => entity.name === matchingDomainEvent.domainEventEntity)?.id
              if (entityId) {
                const schemaIndex = updatedSchemas.findIndex((s) => s.id === item.schemaId)
                if (schemaIndex !== -1 && !updatedSchemas[schemaIndex].entityId) {
                  updatedSchemas[schemaIndex].entityId = entityId
                  schemasNeedUpdate = true
                }
              }
            }
          }

          return item
        })
      })

      await workflowApi.updateWorkflow({
        svgGraph: updatedSvgGraph,
        workflow: workflow.value,
        updateNote: {
          action: 'updated root entity',
          target: ''
        }
      }, true)

      await nextTick()
    }

    if (schemasNeedUpdate) {
      await workflowApi.updateSchemas({
        schemas: updatedSchemas,
        workflow: workflow.value
      })
    }
  }

  const linkCommandAndQueryFieldReferences = async () => {
    const schemas = workflow.value.schemas || []
    const entities = schemas.filter((s) => s.type === SchemaType.ENTITY)
    const events = workflow.value.domainEvents || []

    if (entities.length === 0) {
      return
    }

    const entityByIdMap = new Map()
    for (const entity of entities) {
      entityByIdMap.set(entity.id, entity)
    }

    const entityNameToIdMap = new Map()
    for (const entity of entities) {
      const camelCaseName = toCamelCase(entity.name)
      entityNameToIdMap.set(camelCaseName, entity.id)
    }

    const commandSchemaToEntityMap = new Map()
    for (const event of events) {
      const cards = svgGraph.value[event.id]?.cards || []
      const commandCard = cards.find((c) => c.cardType?.domainModelRole === DomainModelRole.COMMAND)
      const entityCard = cards.find((c) => c.cardType?.domainModelRole === DomainModelRole.AGGREGATE_ROOT)

      if (commandCard?.schemaId && entityCard?.schemaId) {
        commandSchemaToEntityMap.set(commandCard.schemaId, entityCard.schemaId)
      }
    }

    let needsUpdate = false
    const updatedSchemas = safeStructuredClone(schemas)

    const processFields = (fields, fieldRefMap, entityNameToIdMap) => {
      for (const field of fields || []) {
        const camelCaseFieldName = toCamelCase(field.name)
        const matchingEntityId = findEntityIdByName(camelCaseFieldName, entityNameToIdMap)

        if (matchingEntityId && field.fields && field.fields.length > 0 && !field.relatedEntityId) {
          field.relatedEntityId = matchingEntityId
          needsUpdate = true
        }

        const relatedEntityId = fieldRefMap.get(field.name.toLowerCase())
        if (relatedEntityId && !field.relatedEntityId) {
          field.relatedEntityId = relatedEntityId
          needsUpdate = true
        }

        if (field.fields && field.fields.length > 0) {
          let childFieldRefMap = fieldRefMap
          if (field.relatedEntityId) {
            const relatedEntity = entityByIdMap.get(field.relatedEntityId)
            if (relatedEntity) {
              childFieldRefMap = workflowHelpers.methods.getEntityFieldRefMap(relatedEntity)
            }
          }
          processFields(field.fields, childFieldRefMap, entityNameToIdMap)
        }
      }
    }

    for (const schema of updatedSchemas) {
      let targetEntity = null

      if (schema.type === SchemaType.COMMAND) {
        const entityId = commandSchemaToEntityMap.get(schema.id)
        if (entityId) {
          targetEntity = entityByIdMap.get(entityId)
        }
      } else if (schema.type === SchemaType.QUERY && schema.entityId) {
        targetEntity = entityByIdMap.get(schema.entityId)
      }

      if (!targetEntity) {
        continue
      }

      const fieldRefMap = workflowHelpers.methods.getEntityFieldRefMap(targetEntity)
      processFields(schema.fields, fieldRefMap, entityNameToIdMap)
    }

    if (needsUpdate) {
      await workflowApi.updateSchemas({
        schemas: updatedSchemas,
        workflow: workflow.value
      })
    }
  }

  const wait100ms = async () => {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve()
      }, 100)
    })
  }

  const ensureNextVersion = async (oldVersion) => {
    while (oldVersion === workflow.value.version) {
      await wait100ms()
    }
  }

  const getEventFromEventObj = (obj) => {
    const cardLaneId = Object.values(svgLanes.value).find((lane) => lane.name === obj.role).id
    return workflow.value.domainEvents.find(
      (event) => event.description === obj.description && event.laneId === cardLaneId)
  }

  const updateRouteWithEventId = async (eventId) => {
    await router.push({ path: '/workflow/' + workflow.value.projectId + '/' + workflow.value.id + '/' + eventId })
    scrollToEvent(eventId)
  }

  const splitLargeFileContent = (fileContent, maxLinesPerChunk = 1500) => {
    const lines = fileContent.split('\n')
    const chunks = []
    for (let i = 0; i < lines.length; i += maxLinesPerChunk) {
      chunks.push(lines.slice(i, i + maxLinesPerChunk).join('\n'))
    }
    return chunks
  }

  const splitAndAddLargeFile = (file, maxLineLimit = 1500) => {
    const codeChunks = []

    const contentChunks = splitLargeFileContent(file.content, maxLineLimit)
    contentChunks.forEach((chunkContent, index) => {
      codeChunks.push([{
        path: file.path,
        name: `${file.name} (Part ${index + 1}/${contentChunks.length})`,
        content: chunkContent
      }])
    })

    return codeChunks
  }

  const buildCodeChunks = (codeSnippets, chunkLineLimit = 1500) => {
    if (!codeSnippets || codeSnippets.length === 0) {
      return []
    }

    const allCodeChunks = []
    const CHUNK_LINE_LIMIT = chunkLineLimit

    let currentChunk = []
    let currentChunkLineCount = 0

    for (const file of codeSnippets) {
      if (!file.content) {
        continue
      }

      const fileLineCount = file.content.split('\n').length

      if (fileLineCount > CHUNK_LINE_LIMIT) {
        if (currentChunk.length > 0) {
          allCodeChunks.push(currentChunk)
          currentChunk = []
          currentChunkLineCount = 0
        }
        const chunks = splitAndAddLargeFile(file, CHUNK_LINE_LIMIT)

        allCodeChunks.push(...chunks)
      } else {
        if (currentChunkLineCount + fileLineCount > CHUNK_LINE_LIMIT) {
          allCodeChunks.push(currentChunk)
          currentChunk = [file]
          currentChunkLineCount = fileLineCount
        } else {
          currentChunk.push(file)
          currentChunkLineCount += fileLineCount
        }
      }
    }

    if (currentChunk.length > 0) {
      allCodeChunks.push(currentChunk)
    }

    return allCodeChunks
  }

  const buildWorkflowFromCode = async (codeSnippets, description, onSuccess) => {
    try {
      await store.dispatch('workflow/clearFailedGenerations')

      const allCodeChunks = buildCodeChunks(codeSnippets, 1500)
      const workflowStepsMap = new Map()
      const totalChunks = allCodeChunks.length

      for (let i = 0; i < totalChunks; i++) {
        const chunkOfSnippets = allCodeChunks[i]

        proxy.$loadingState.setMessage(`Analyzing code... (Step ${i + 1} of ${totalChunks})`)

        try {
          const previouslyExtractedWorkflowSteps = Array.from(workflowStepsMap.values())

          const deltaResponse = await generateWorkflowFromCode({
            workflow: workflow.value,
            workflowSteps: previouslyExtractedWorkflowSteps,
            codeSnippets: chunkOfSnippets,
            description,
            stream: false
          })

          if (deltaResponse && deltaResponse.updatesAndAdditions) {
            for (const updatedStep of deltaResponse.updatesAndAdditions) {
              const stepKey = `${updatedStep.role}|${updatedStep.eventDescription}`
              workflowStepsMap.set(stepKey, updatedStep)
            }
          }
        } catch (error) {
          if (error.name !== 'AbortError') {
            await store.dispatch('workflow/addFailedGeneration', {
              type: 'generate-workflow',
              name: `Code Analysis (Step ${i + 1} of ${totalChunks})`
            })
          }
        }
      }

      const workflowSteps = Array.from(workflowStepsMap.values())
      const hasFailures = failedGenerations.value.length > 0

      if (!workflowSteps?.length || hasFailures) {
        if (hasFailures) {
          await store.dispatch('workflow/openRetryDialog', {
            failedItems: failedGenerations.value,
            retryCallback: () => buildWorkflowFromCode(codeSnippets, description, onSuccess)
          })
          proxy.$loadingState.stop()
        }
        return
      }

      let sortedWorkflowSteps = workflowSteps

      try {
        const sortResponse = await generateSortEvents({
          workflow: workflow.value,
          events: workflowSteps.map((step) => ({ role: step.role, description: step.eventDescription })),
          stream: false
        })

        const sortedEvents = sortResponse?.events

        if (sortedEvents && Array.isArray(sortedEvents)) {
          const sorted = []
          sortedEvents.forEach((sortedEvent) => {
            const key = `${sortedEvent.role}|${sortedEvent.description}`
            if (workflowStepsMap.has(key)) {
              sorted.push(workflowStepsMap.get(key))
            }
          })

          if (sorted.length < workflowSteps.length) {
            const sortedKeys = new Set(sortedEvents.map((e) => `${e.role}|${e.description}`))
            workflowSteps.forEach((originalStep) => {
              const key = `${originalStep.role}|${originalStep.eventDescription}`
              if (!sortedKeys.has(key)) {
                sorted.push(originalStep)
              }
            })
          }

          sortedWorkflowSteps = sorted
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          await store.dispatch('workflow/addFailedGeneration', {
            type: 'generate-events',
            name: 'Sort Workflow Steps'
          })
        }
      }

      if (failedGenerations.value.length > 0) {
        await store.dispatch('workflow/openRetryDialog', {
          failedItems: failedGenerations.value,
          retryCallback: () => buildWorkflowFromCode(codeSnippets, description, onSuccess)
        })
      } else {
        onSuccess(sortedWorkflowSteps)
      }
    } catch (e) {
      if (e.name !== 'AbortError') {
        console.error(e)
      }
    } finally {
      proxy.$loadingState.stop()
    }
  }

  const generateExampleData = async () => {
    const allSchemas = workflow.value.schemas ?? []
    const entitySchemas = allSchemas.filter((schema) => schema.type === SchemaType.ENTITY)

    const entities = entitySchemas.map((entity) => ({
      name: entity.name,
      fields: (entity.fields ?? []).map((field) => ({
        name: field.name,
        exampleData: field.exampleData
      }))
    }))

    if (entities.length) {
      const exampleDataResponse = await generateEntitiesExampleData({
        aiVersion: workflow.value.aiVersion,
        entities
      })

      if (exampleDataResponse?.entities?.length > 0) {
        const entitiesFromLLM = exampleDataResponse.entities

        const llmEntityByName = new Map()
        for (const llmEntity of entitiesFromLLM) {
          if (llmEntity.name) {
            llmEntityByName.set(llmEntity.name.toLowerCase(), llmEntity)
          }
        }

        const entitySchemaIds = new Set(entitySchemas.map((e) => e.id))

        const updatedSchemas = allSchemas.map((schema) => {
          if (!entitySchemaIds.has(schema.id)) {
            return schema
          }

          const llmEntity = llmEntityByName.get(schema.name?.toLowerCase())

          if (!llmEntity) {
            return schema
          }

          const llmFieldByName = new Map()
          for (const llmField of llmEntity.fields || []) {
            if (llmField.name) {
              llmFieldByName.set(llmField.name.toLowerCase(), llmField)
            }
          }

          return {
            ...schema,
            fields: (schema.fields ?? []).map((field) => {
              const llmField = llmFieldByName.get(field.name?.toLowerCase())

              return {
                ...field,
                exampleData: llmField?.exampleData || field.exampleData
              }
            })
          }
        })

        await workflowApi.updateSchemas({
          schemas: updatedSchemas,
          workflow: workflow.value
        })
      }
    }
  }

  const createBoundedContext = async (githubInfo) => {
    const boundedContext = {
      id: randomUUID(),
      name: workflow.value.name,
      githubAccountLogin: githubInfo.githubAccountLogin || undefined,
      githubRepo: githubInfo.githubRepo || undefined,
      githubBranch: githubInfo.githubBranch || undefined,
      templateId: 'Legacy Project'
    }

    await store.dispatch('workflow/updateWorkflow', {
      projectId: workflow.value.projectId,
      input: {
        id: workflow.value.id,
        expectedVersion: workflow.value.version,
        boundedContexts: [...(workflow.value.boundedContexts || []), boundedContext],
        updateNote: {
          action: 'added bounded context',
          target: workflow.value.name
        }
      }
    })

    const updatedEntities = (workflow.value.schemas ?? []).map((schema) => ({
      ...schema,
      boundedContext: schema.type === SchemaType.ENTITY ? boundedContext.id : undefined
    }))

    await store.dispatch('workflow/updateWorkflow', {
      projectId: workflow.value.projectId,
      input: {
        id: workflow.value.id,
        expectedVersion: workflow.value.version,
        schemas: updatedEntities,
        updateNote: {
          action: 'updated bounded context on entity',
          target: ''
        }
      }
    })

    return boundedContext
  }

  const saveWorkflowSnapshot = async (boundedContextId) => {
    const allSchemas = workflow.value.schemas || []
    const allDomainEvents = workflow.value.domainEvents || []

    const baselineWorkflow = {
      schemas: [],
      domainEvents: allDomainEvents.map((event) => ({
        ...event,
        cards: (event.cards || []).filter((card) => {
          if (!card.cardType) {
            return false
          }
          const domainModelRole = card.cardType.domainModelRole

          return !domainModelRole || ![
            DomainModelRole.COMMAND,
            DomainModelRole.READ_MODEL,
            DomainModelRole.GIVEN_WHEN_THEN
          ].includes(domainModelRole)
        })
      }))
    }

    const entityIds = new Set(
      allSchemas
        .filter((s) => s.type === SchemaType.ENTITY && s.boundedContext === boundedContextId)
        .map((s) => s.id)
    )

    const domainIds = [...entityIds]

    for (const event of allDomainEvents) {
      const entityCard = (event.cards || []).find(
        (c) => c.cardType?.domainModelRole === DomainModelRole.AGGREGATE_ROOT
      )
      if (entityCard && entityIds.has(entityCard.schemaId)) {
        for (const card of event.cards || []) {
          const role = card.cardType?.domainModelRole
          if (role === DomainModelRole.COMMAND ||
              role === DomainModelRole.READ_MODEL ||
              role === DomainModelRole.GIVEN_WHEN_THEN) {
            domainIds.push(card.id)
          }
        }
      }
    }

    const updatedWorkflow = safeStructuredClone(baselineWorkflow)

    for (const domainId of domainIds) {
      const currentEntity = allSchemas.find((m) => m.id === domainId)
      if (currentEntity) {
        const index = updatedWorkflow.schemas.findIndex((m) => m.id === domainId)
        if (index !== -1) {
          updatedWorkflow.schemas[index] = currentEntity
        } else {
          updatedWorkflow.schemas.push(currentEntity)
        }
      }

      for (const event of allDomainEvents) {
        const currentCard = (event.cards || []).find((r) => r.id === domainId)
        if (currentCard) {
          let updatedCard = safeStructuredClone(currentCard)

          if (currentCard.cardType?.domainModelRole === DomainModelRole.COMMAND) {
            const gwtCards = (event.cards || []).filter(
              (c) => c.cardType?.domainModelRole === DomainModelRole.GIVEN_WHEN_THEN
            )
            const gwtDescriptions = gwtCards.map((c) => sanitizeCardDescription(c.description))
            updatedCard = { ...currentCard, gwtDescriptions }
          }

          const oldEvent = updatedWorkflow.domainEvents.find((e) => e.id === event.id)
          if (oldEvent) {
            const idx = (oldEvent.cards || []).findIndex((r) => r.id === domainId)
            if (idx !== -1) {
              oldEvent.cards[idx] = updatedCard
            } else {
              if (!oldEvent.cards) {
                oldEvent.cards = []
              }
              oldEvent.cards.push(updatedCard)
            }
          } else {
            updatedWorkflow.domainEvents.push({
              ...event,
              cards: [updatedCard]
            })
          }

          const currentSchema = currentCard.schemaId
            ? allSchemas.find((s) => s.id === currentCard.schemaId)
            : null
          if (currentSchema) {
            const schemaIdx = updatedWorkflow.schemas.findIndex((s) => s.id === currentSchema.id)
            if (schemaIdx !== -1) {
              updatedWorkflow.schemas[schemaIdx] = safeStructuredClone(currentSchema)
            } else {
              updatedWorkflow.schemas.push(safeStructuredClone(currentSchema))
            }
          }
        }
      }
    }

    await store.dispatch('boundedContexts/createObject', {
      filepath: '__workflow',
      boundedContextId,
      prompt: '',
      contents: JSON.stringify(updatedWorkflow),
      tags: []
    })
  }

  const saveFiles = async (boundedContext, files) => {
    for (const file of files) {
      const object = {
        filepath: `root/${file.path}`,
        boundedContextId: boundedContext.id,
        prompt: '',
        contents: file.content || '',
        tags: file.tags || []
      }

      await store.dispatch('boundedContexts/createObject', object)
    }
  }

  const getTaggedFiles = async (files) => {
    const allQueries = []
    const allCommands = []
    const allEntities = (workflow.value.schemas || []).map((entity) => ({
      id: entity.id,
      name: entity.name
    }))

    for (const event of workflow.value.domainEvents) {
      for (const card of event.cards) {
        if (card.cardType?.domainModelRole === DomainModelRole.READ_MODEL) {
          allQueries.push({ id: card.id, name: sanitizeCardDescription(card.description || '') })
        }
        if (card.cardType?.domainModelRole === DomainModelRole.COMMAND) {
          allCommands.push({ id: card.id, name: sanitizeCardDescription(card.description || '') })
        }
      }
    }

    const workflowData = {
      commands: allCommands,
      readModels: allQueries,
      entities: allEntities
    }

    const filteredFiles = files.filter((file) => isFileRelevantForAnalysis(file.path))
    const allCodeChunks = buildCodeChunks(filteredFiles, 1500)
    const allTaggedFileResponses = []
    const totalChunks = allCodeChunks.length

    for (let i = 0; i < totalChunks; i++) {
      proxy.$loadingState.setMessage(`Tagging code files with AI... (Step ${i + 1} of ${totalChunks})`)
      const chunkOfSnippets = allCodeChunks[i]

      try {
        const taggingResponse = await generateFileTagging({
          aiVersion: workflow.value.aiVersion,
          workflowData,
          codeSnippets: chunkOfSnippets
        })

        if (taggingResponse && taggingResponse.fileTags) {
          allTaggedFileResponses.push(...taggingResponse.fileTags)
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          await store.dispatch('workflow/addFailedGeneration', {
            type: 'file-tagging',
            name: `File Tagging (Step ${i + 1} of ${totalChunks})`
          })
        }
      }
    }

    const fileTagsMap = new Map()

    for (const taggedFile of allTaggedFileResponses) {
      const path = taggedFile.path

      const tags = fileTagsMap.get(path) || new Set()
      for (const tag of taggedFile.tags) {
        tags.add(tag)
      }

      fileTagsMap.set(path, tags)
    }

    return files.map((file) => {
      const tagsSet = fileTagsMap.get(file.path)
      const fileTags = tagsSet ? Array.from(tagsSet) : []

      return { ...file, tags: fileTags }
    })
  }

  const retryCreateWorkflowAI = async (workflowSteps, filesData, githubInfo) => {
    proxy.$loadingState.start('Retrying failed generation steps...')

    const itemsToRetry = [...failedGenerations.value]
    await store.dispatch('workflow/clearFailedGenerations')

    const retriedTypes = new Set()

    for (const item of itemsToRetry) {
      if (retriedTypes.has(item.type)) {
        continue
      }

      retriedTypes.add(item.type)

      try {
        if (item.type === 'example-data') {
          proxy.$loadingState.setMessage('Generating example data...')
          await generateExampleData()
        } else if (item.type === 'file-tagging') {
          const { allFiles, filesToTag } = filesData || {}
          if (allFiles?.length && filesToTag?.length) {
            proxy.$loadingState.setMessage('Tagging code files...')
            const filesWithTags = await getTaggedFiles(filesToTag)

            const hasFileTaggingFailures = failedGenerations.value.some((f) => f.type === 'file-tagging')

            if (!hasFileTaggingFailures) {
              const boundedContext = (workflow.value.boundedContexts || []).find(
                (bc) => bc.githubRepo === githubInfo?.githubRepo && bc.githubBranch === githubInfo?.githubBranch
              )
              if (boundedContext) {
                const filesToSave = allFiles.map((file) => {
                  const matchingTaggedFile = filesWithTags.find((f) => f.path === file.path)
                  return matchingTaggedFile || file
                })
                await saveFiles(boundedContext, filesToSave)
              }
            }
          }
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          await store.dispatch('workflow/addFailedGeneration', item)
        }
      }
    }

    proxy.$loadingState.stop()

    const hasFailures = failedGenerations.value.length > 0
    if (hasFailures) {
      await store.dispatch('workflow/openRetryDialog', {
        failedItems: failedGenerations.value,
        retryCallback: () => retryCreateWorkflowAI(workflowSteps, filesData, githubInfo)
      })
    } else {
      store.commit('snackbar/showMessage', {
        content: 'All steps completed successfully!',
        timeout: 3000,
        color: 'green'
      }, { root: true })
    }
  }

  const createWorkflow = async (workflowSteps, filesData, githubInfo) => {
    try {
      await store.dispatch('workflow/clearFailedGenerations')

      let commandCardKey
      let queryCardKey
      let entityCardKey
      let domainEventCardKey
      let gwtCardKey

      for (let i = 0; i < cardTypes.value?.length; i++) {
        const type = cardTypes.value[i]

        if (type.generateWithAI && type.domainModelRole) {
          const cardType = formatCardKey(type.title)

          switch (type.domainModelRole) {
            case DomainModelRole.COMMAND:
              commandCardKey = cardType
              break
            case DomainModelRole.READ_MODEL:
              queryCardKey = cardType
              break
            case DomainModelRole.AGGREGATE_ROOT:
              entityCardKey = cardType
              break
            case DomainModelRole.DOMAIN_EVENT:
              domainEventCardKey = cardType
              break
            case DomainModelRole.GIVEN_WHEN_THEN:
              gwtCardKey = cardType
              break
            default:
              break
          }
        }
      }

      const queriesForLinking = []
      const domainEventsForLinking = []
      const allEntities = []

      for (const step of workflowSteps) {
        if (!step) {
          continue
        }

        const eventFromLLM = { description: step.eventDescription, role: step.role }
        const cardFromLLM = { ...eventFromLLM }

        if (commandCardKey && step.command?.name) {
          cardFromLLM[commandCardKey] = step.command.name
        }

        if (queryCardKey && step.queries?.length) {
          cardFromLLM[queryCardKey] = step.queries.map((query) => ({
            name: query.name
          }))
        }

        if (entityCardKey && step.entities?.length) {
          const rootEntity = step.entities.find((entity) => entity.isAggregateRoot)

          if (rootEntity) {
            cardFromLLM[entityCardKey] = rootEntity.name
          }
        }

        if (domainEventCardKey && step.domainEventSchema?.name) {
          cardFromLLM[domainEventCardKey] = step.domainEventSchema.name
        }

        if (gwtCardKey && step.gwt?.length) {
          cardFromLLM[gwtCardKey] = step.gwt
        }

        await addEvent(eventFromLLM)

        await addCard(cardFromLLM)

        if (step.command) {
          const commandFromLLM = { ...eventFromLLM, fields: step.command.fields, commandName: step.command.name }
          await addCommandSchema(commandFromLLM)
        }

        if (step.queries?.length) {
          for (const query of step.queries) {
            const queryFromLLM = {
              ...eventFromLLM,
              queryFields: query.fields,
              queryName: query.name,
              queriedEntity: query.queriedEntity,
              cardinality: query.cardinality
            }
            const queryInfo = await addQuerySchema(queryFromLLM)
            if (queryInfo) {
              queriesForLinking.push(queryInfo)
            }
          }
        }

        if (step.domainEventSchema) {
          const domainEventFromLLM = {
            ...eventFromLLM,
            domainEventFields: step.domainEventSchema.fields,
            domainEventName: step.domainEventSchema.name,
            domainEventEntity: step.domainEventSchema.domainEventEntity
          }
          const domainEventInfo = await addDomainEventSchema(domainEventFromLLM)
          if (domainEventInfo) {
            domainEventsForLinking.push(domainEventInfo)
          }
        }

        if (step.entities?.length) {
          for (const entity of step.entities) {
            allEntities.push({ name: entity.name, fields: entity.fields })
          }
        }
      }

      await addEntities(allEntities)

      proxy.$loadingState.setMessage('Finalizing domain model...')
      await nextTick()
      const versionBeforeLinking = workflow.value.version
      await linkEntities(queriesForLinking, domainEventsForLinking)
      await ensureNextVersion(versionBeforeLinking)
      await linkCommandAndQueryFieldReferences()

      try {
        await generateExampleData()
      } catch (error) {
        if (error.name !== 'AbortError') {
          await store.dispatch('workflow/addFailedGeneration', {
            type: 'example-data',
            name: 'Example Data Generation'
          })
        }
      }

      const boundedContext = await createBoundedContext(githubInfo)

      await saveWorkflowSnapshot(boundedContext.id)

      const { allFiles, filesToTag } = filesData || {}

      if (allFiles?.length && filesToTag?.length) {
        proxy.$loadingState.setMessage('Tagging code files...')
        const filesWithTags = await getTaggedFiles(filesToTag)

        const hasFileTaggingFailures = failedGenerations.value.some((f) => f.type === 'file-tagging')

        if (!hasFileTaggingFailures) {
          const filesToSave = allFiles.map((file) => {
            const matchingTaggedFile = filesWithTags.find((f) => f.path === file.path)
            return matchingTaggedFile || file
          })
          await saveFiles(boundedContext, filesToSave)
        }
      }

      const hasFailures = failedGenerations.value.length > 0
      if (hasFailures) {
        await store.dispatch('workflow/openRetryDialog', {
          failedItems: failedGenerations.value,
          retryCallback: () => retryCreateWorkflowAI(workflowSteps, filesData, githubInfo)
        })
      }
    } catch (e) {
      console.log(e)
    }
  }

  return { buildWorkflowFromCode, createWorkflow }
}

export default useGenerateWorkflow
